/**
 * Cake Details Form Component
 * 
 * Collects customization details for custom cake orders.
 * Shown in cart when cake items are present.
 */

import { useState, useEffect } from 'react';

// Cake customization options
const CAKE_SIZES = ['6" (8-10 servings)', '8" (12-16 servings)', '10" (20-24 servings)', 'Sheet (24-30 servings)', 'Cupcakes (12)'];
const CAKE_FLAVORS = ['Vanilla', 'Chocolate', 'Red Velvet', 'Marble', 'Lemon', 'Carrot', 'Funfetti', 'Other'];
const CAKE_FILLINGS = ['Buttercream', 'Cream Cheese', 'Chocolate Ganache', 'Strawberry', 'Raspberry', 'Lemon Curd', 'None', 'Other'];
const CAKE_OCCASIONS = ['Birthday', 'Wedding', 'Baby Shower', 'Graduation', 'Anniversary', 'Corporate Event', 'Holiday', 'Other'];

export interface CakeDetails {
  size: string;
  flavor: string;
  filling: string;
  occasion: string;
  cakeMessage: string;      // Text to write on cake
  designNotes: string;      // Colors, theme, special requests
  servings: string;         // Approximate number
  dietaryNotes: string;     // Allergies, dietary restrictions
}

interface CakeDetailsFormProps {
  initialDetails?: Partial<CakeDetails>;
  onChange: (details: CakeDetails) => void;
  compact?: boolean; // Compact mode for cart sidebar
}

const defaultDetails: CakeDetails = {
  size: '',
  flavor: '',
  filling: '',
  occasion: '',
  cakeMessage: '',
  designNotes: '',
  servings: '',
  dietaryNotes: '',
};

export default function CakeDetailsForm({ 
  initialDetails, 
  onChange,
  compact = false 
}: CakeDetailsFormProps) {
  const [details, setDetails] = useState<CakeDetails>({
    ...defaultDetails,
    ...initialDetails,
  });

  // Notify parent of changes
  useEffect(() => {
    onChange(details);
  }, [details, onChange]);

  const handleChange = (field: keyof CakeDetails, value: string) => {
    setDetails(prev => ({ ...prev, [field]: value }));
  };

  const inputClass = compact 
    ? "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
    : "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent";

  const labelClass = compact
    ? "block text-xs font-medium text-gray-700 mb-1"
    : "block text-sm font-medium text-gray-700 mb-2";

  return (
    <div className={`cake-details-form ${compact ? 'space-y-3' : 'space-y-4'}`}>
      {/* Header */}
      <div className={`bg-amber-50 border border-amber-200 rounded-lg ${compact ? 'p-3' : 'p-4'}`}>
        <h3 className={`font-semibold text-amber-800 ${compact ? 'text-sm' : 'text-base'}`}>
          🎂 Cake Customization Details
        </h3>
        <p className={`text-amber-700 ${compact ? 'text-xs mt-1' : 'text-sm mt-2'}`}>
          Please fill in details so we can create your perfect cake. We'll contact you to confirm.
        </p>
      </div>

      {/* Size & Servings Row */}
      <div className={`grid ${compact ? 'grid-cols-1 gap-3' : 'grid-cols-2 gap-4'}`}>
        <div>
          <label className={labelClass}>Cake Size *</label>
          <select
            value={details.size}
            onChange={(e) => handleChange('size', e.target.value)}
            className={inputClass}
            required
          >
            <option value="">Select size...</option>
            {CAKE_SIZES.map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Approx. Servings</label>
          <input
            type="text"
            value={details.servings}
            onChange={(e) => handleChange('servings', e.target.value)}
            placeholder="e.g., 15 people"
            className={inputClass}
          />
        </div>
      </div>

      {/* Flavor & Filling Row */}
      <div className={`grid ${compact ? 'grid-cols-1 gap-3' : 'grid-cols-2 gap-4'}`}>
        <div>
          <label className={labelClass}>Cake Flavor *</label>
          <select
            value={details.flavor}
            onChange={(e) => handleChange('flavor', e.target.value)}
            className={inputClass}
            required
          >
            <option value="">Select flavor...</option>
            {CAKE_FLAVORS.map(flavor => (
              <option key={flavor} value={flavor}>{flavor}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Filling</label>
          <select
            value={details.filling}
            onChange={(e) => handleChange('filling', e.target.value)}
            className={inputClass}
          >
            <option value="">Select filling...</option>
            {CAKE_FILLINGS.map(filling => (
              <option key={filling} value={filling}>{filling}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Occasion */}
      <div>
        <label className={labelClass}>Occasion</label>
        <select
          value={details.occasion}
          onChange={(e) => handleChange('occasion', e.target.value)}
          className={inputClass}
        >
          <option value="">Select occasion...</option>
          {CAKE_OCCASIONS.map(occasion => (
            <option key={occasion} value={occasion}>{occasion}</option>
          ))}
        </select>
      </div>

      {/* Message on Cake */}
      <div>
        <label className={labelClass}>Message on Cake</label>
        <input
          type="text"
          value={details.cakeMessage}
          onChange={(e) => handleChange('cakeMessage', e.target.value)}
          placeholder='e.g., "Happy Birthday Sarah!"'
          className={inputClass}
          maxLength={50}
        />
        <p className="text-xs text-gray-500 mt-1">Max 50 characters</p>
      </div>

      {/* Design Notes */}
      <div>
        <label className={labelClass}>Design Notes / Theme</label>
        <textarea
          value={details.designNotes}
          onChange={(e) => handleChange('designNotes', e.target.value)}
          placeholder="Describe colors, theme, decorations, or attach inspiration photo link..."
          className={`${inputClass} resize-none`}
          rows={compact ? 2 : 3}
        />
      </div>

      {/* Dietary Notes */}
      <div>
        <label className={labelClass}>Dietary Notes / Allergies</label>
        <input
          type="text"
          value={details.dietaryNotes}
          onChange={(e) => handleChange('dietaryNotes', e.target.value)}
          placeholder="e.g., Nut-free, gluten-free, dairy-free..."
          className={inputClass}
        />
      </div>

      {/* Note about pricing */}
      <div className={`bg-gray-50 rounded-lg ${compact ? 'p-3' : 'p-4'}`}>
        <p className={`text-gray-600 ${compact ? 'text-xs' : 'text-sm'}`}>
          <strong>Note:</strong> The price shown is a starting price. Final price may vary based on design complexity. 
          We'll contact you within 24 hours to confirm details and final pricing before payment.
        </p>
      </div>
    </div>
  );
}

